import React, { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setUserOnline } from "../../../services/storage/LocalStorage";
import {
    setBannerVisibility,
    setCounter,
} from "../../../state/actions/offlineBanner";
import useRealOnlineStatus from "../../../utils/customHooks/useRealOnlineStatus";
import OfflineBanner from "./OfflineBanner";
import SlowNetworkBanner from "./SlowNetworkBanner";

const Banner = () => {
    const dispatch = useDispatch();
    const [slowInternet, setSlowInternet] = useState(false);
    const { show } = useSelector((state) => state.appNotification);
    const latestAPIRequest = useSelector((state) => state.offlineBanner.latestAPIRequest);
    const counter = useSelector((state) => state.offlineBanner.counter);
    const visibility = useSelector((state) => state.offlineBanner.visibility);
    
    // Use the real online status hook
    const { isOnline } = useRealOnlineStatus();
    const intervalIdRef = useRef(null);
    const prevIsOnlineRef = useRef(isOnline);
    const counterRef = useRef(counter);
    const isOnlineRef = useRef(isOnline);

    const refreshImages = () => {
        const images = document.querySelectorAll("img");
        images.forEach((img) => {
            const src = img.getAttribute("src");
            if (src) {
                img.src = src.split("?")[0] + `?t=${Date.now()}`;
            }
        });
    };

    const checkSlowConnection = useCallback(() => {
        const connection = navigator.connection;
        if (connection && typeof connection.downlink === "number") {
            if (connection.downlink < 0.5) {
                setSlowInternet(true);
                dispatch(setBannerVisibility(false))
            } else {
                setSlowInternet(false);
                dispatch(setBannerVisibility(false))
            }
        }
    }, [dispatch]);

    const changeNetworkHandler = useCallback(() => {
        checkSlowConnection();
    }, [checkSlowConnection]);

    // Keep refs in sync
    useEffect(() => {
        counterRef.current = counter;
    }, [counter]);

    useEffect(() => {
        isOnlineRef.current = isOnline;
    }, [isOnline]);

    const startCountdown = useCallback(() => {
        if (intervalIdRef.current) {
            clearInterval(intervalIdRef.current);
        }
        const tick = () => {
            const currentCounter = counterRef.current;
            const currentIsOnline = isOnlineRef.current;
            if (currentCounter === 0) {
                // When counter reaches 0, check connection again
                // The hook will handle the actual check, we just need to verify
                if (currentIsOnline) {
                    clearInterval(intervalIdRef.current);
                    intervalIdRef.current = null;
                    dispatch(setBannerVisibility(false));
                    dispatch(setCounter(5));
                    setUserOnline(true);
                } else {
                    // Reset counter and restart countdown
                    clearInterval(intervalIdRef.current);
                    dispatch(setBannerVisibility(true))
                        .then(() => {
                            return dispatch(setCounter(5));
                        })
                        .then(() => {
                            // Restart countdown after reset
                            if (intervalIdRef.current) {
                                clearInterval(intervalIdRef.current);
                            }
                            intervalIdRef.current = setInterval(tick, 1000);
                        });
                    setUserOnline(false);
                }
            } else {
                dispatch(setCounter(currentCounter - 1));
            }
        };
        intervalIdRef.current = setInterval(tick, 1000);
    }, [dispatch]);

    const resetCounter = useCallback((visibility, value) => {
        if (intervalIdRef.current) {
            clearInterval(intervalIdRef.current);
            intervalIdRef.current = null;
        }
        dispatch(setBannerVisibility(visibility))
            .then(() => {
                return dispatch(setCounter(value));
            })
            .then(() => {
                startCountdown();
            });
    }, [dispatch, startCountdown]);

    const closeSlowInternetBanner = () => {
        dispatch(setBannerVisibility(true));
        setSlowInternet(false);
    };

    // Handle online/offline status changes
    useEffect(() => {
        const prevIsOnline = prevIsOnlineRef.current;
        
        // Only react to actual changes
        if (prevIsOnline === isOnline) {
            return;
        }

        prevIsOnlineRef.current = isOnline;

        if (!isOnline) {
            // When going offline
            dispatch(setBannerVisibility(true));
            resetCounter(true, 5);
            setUserOnline(false);
        } else {
            // When going online
            refreshImages();
            if (intervalIdRef.current) {
                clearInterval(intervalIdRef.current);
                intervalIdRef.current = null;
            }
            dispatch(setBannerVisibility(false)).then(() => {
                dispatch(setCounter(5));
            });
            setUserOnline(true);
            window.dispatchEvent(new Event("stream_closed"));
        }
    }, [isOnline, dispatch, resetCounter]);

    // Set up slow connection monitoring
    useEffect(() => {
        checkSlowConnection();

        if (navigator.connection) {
            navigator.connection.addEventListener("change", changeNetworkHandler);
        }

        return () => {
            if (navigator.connection) {
                navigator.connection.removeEventListener("change", changeNetworkHandler);
            }
        };
    }, [checkSlowConnection, changeNetworkHandler]);

    // Cleanup interval on unmount
    useEffect(() => {
        return () => {
            if (intervalIdRef.current) {
                clearInterval(intervalIdRef.current);
            }
        };
    }, []);

    useEffect(() => {
        const disableButtons = () => {
            const trash = document.getElementsByClassName("mapbox-gl-draw_trash");
            const polygon = document.getElementsByClassName("mapbox-gl-draw_polygon");
            if (visibility) {
                if (trash.length) trash[0].setAttribute("disabled", "");
                if (polygon.length) polygon[0].setAttribute("disabled", "");
            } else {
                if (trash.length) trash[0].removeAttribute("disabled");
                if (polygon.length) polygon[0].removeAttribute("disabled");
            }
        };
        disableButtons();
    }, [visibility]);

    if (visibility) return <OfflineBanner />;
    if (show) return null; // Placeholder for app banner
    if (!visibility && slowInternet) {
        return <SlowNetworkBanner closeSlowInternetBanner={closeSlowInternetBanner} />;
    }

    return null;
};

export default Banner;
